import { ApolloError } from "@apollo/client";
import { useRequiredSession } from "src/components/session/session";
import { useRouter } from "next/router";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import {
  useEditorDispatch,
  useEditorParameters,
  useEditorRefresh,
} from "./context";
import { useToolbarState } from "./EditorPageSidebar/toolbarState";
import {
  callUpsertQuery,
  isQueryHasChangedError,
  QueryHasChangedError,
} from "./graphql";
import { EditorQuery } from "./state";

type UseSaveQueryOptions = {
  onQueryHasChangedError: (e: QueryHasChangedError | null) => void;
};

export function useEditorSaveQuery(options: UseSaveQueryOptions) {
  const router = useRouter();
  const { getToolUrl } = useToolbarState();
  const dispatch = useEditorDispatch();
  const parameters = useEditorParameters();
  const refresh = useEditorRefresh();
  const upsertQuery = useUpsertQuery();

  async function saveQuery(query: EditorQuery) {
    if (!parameters) {
      return;
    }

    try {
      const id = await upsertQuery({
        ...query,
        parameters: parameters.defaults,
      });

      dispatch({ type: "saveQuery" });
      await refresh?.();
      return id;
    } catch (error) {
      if (error instanceof ApolloError) {
        const graphQLError = error.graphQLErrors[0];

        if (isQueryHasChangedError(graphQLError)) {
          options.onQueryHasChangedError(graphQLError.extensions);
          router.push(getToolUrl("version-history"));
          return undefined;
        }
      }

      const description =
        error instanceof Error ? error.message : String(error);

      addToastNotification({
        level: "error",
        title: "Query save failed",
        description,
      });

      return undefined;
    }
  }

  return saveQuery;
}

function useUpsertQuery() {
  const session = useRequiredSession();
  return (query: EditorQuery) => callUpsertQuery(query, session);
}
